/** @jsxRuntime automatic @jsxImportSource react */ // [C35] the PDF worker loads these files through tsx outside its own tsconfig
// Extracted from original/src/components/ProfessionalPrint.tsx:105-1594 — the **sales-invoice branch** of the
// `ProfessionalPrint` component that StatementA4 cut (see the header of ./StatementA4.tsx). The old store sale fed it
// through `triggerReceiptPDF` (original/src/App.tsx:12475-12795: title "قائمة بيع #…" / "قائمة بيع بضاعة بالأجل #…",
// the five columns ت / المادة / الكمية / السعر المفرد / المجموع, the buyer name / phone / address, `isCashSale`).
// ADR-08 §2.1 "copy, then cut logic".
// Kept verbatim: the page frame (:884-914), the company header (:915-966) with the buyer phone / address lines in the
// metadata block (:968-1007), the "خلاصة قائمة البيع والتفاصيل" box on the first page (:1013-1083: buyer, invoice
// number, payment method in green / amber, grand total; the extra line with phone, address, cash received,
// remaining), the data table (:1147-1305, plain-cell path), the plain summary grid (:1546-1561), the buyer / cashier
// signature block on the last page (:1564-1576), the footer with page numbering (:1579-1588) and the sales
// pagination (:680-711: 15 rows on a page for a cash sale, 9 when a summary follows, 20 / 9 on later pages).
// Cut: `isCashSaleEffective` / `isSaleInvoice` title and label heuristics (:127-147) → the container says which
// invoice it prints (`cashSale`); `salesSummaryDetails` label matching and the `parseFloat` grand total over the row
// texts (:149-190; ADR-02 §2.9) → preformatted props from the server's read model; `getStructuredSummary` (the
// customer-statement summary of a credit sale, :230-546, :1307-1545) → the plain summary grid with the figures the
// container passes (as StatementA4); the default `companySettings` with the hard-coded brand (:192-201, F-337) →
// required `office` (D-052); `new Date()` issue date → `dateLabel` in the office time zone (D-162); the
// amount-in-words line (`tafqeetNumber`, :1077-1082) — [C35] restored as `wordsLabel`, computed by the server (D-148);
// the bundled logo and its `onError` swap → `office.logoUrl` (as StatementA4).
// Changed by decision: a reprint carries "نسخة" in the title (D-147); a discounted invoice prints its gross and
// discount in the summary grid (D-316 (2)); the buyer comes from the encrypted buyer record (D-314).
// Strings: the old inline texts are `print.sale.*` keys; the shared header / footer texts reuse StatementA4's `print.*`.
import { useMemo } from 'react';
import type { CSSProperties } from 'react';
import { createT } from '@faylasuf/i18n';
import type { Locale } from '@faylasuf/i18n';
import type { PrintOffice, PrintSummaryItem } from './StatementA4.js';

export interface SalesInvoiceA4Line {
  readonly name: string;
  readonly quantity: string;
  /** Preformatted with its currency suffix, e.g. "1,500 د.ع". */
  readonly priceLabel: string;
  readonly totalLabel: string;
}

export interface SalesInvoiceA4Props {
  readonly locale: Locale;
  /** Full title incl. the invoice number and, for a reprint, the copy word. */
  readonly title: string;
  readonly dateLabel: string;
  readonly docNumber: string;
  readonly office: PrintOffice;
  /** Cash sale (paid in full) vs. a sale on a customer account (old `isCashSale`). */
  readonly cashSale: boolean;
  readonly buyerName: string;
  readonly buyerPhone?: string | null;
  readonly buyerAddress?: string | null;
  readonly paymentMethodLabel: string;
  readonly grandTotalLabel: string;
  readonly receivedLabel?: string | null;
  readonly remainingLabel?: string | null;
  readonly lines: readonly SalesInvoiceA4Line[];
  /** Extra figures printed in the plain summary grid on the last page (gross / discount, account figures). */
  readonly summary?: readonly PrintSummaryItem[];
  /** [C35] D-148 amount in words of the grand total (server, exact decimals); the old line under the box (:1082-1087). */
  readonly wordsLabel?: string | null;
}

interface PageInfo {
  readonly data: readonly SalesInvoiceA4Line[];
  readonly showSummary: boolean;
}

// Sales pagination (:680-711)
const getPagesInfo = (data: readonly SalesInvoiceA4Line[], hasStructured: boolean): PageInfo[] => {
  if (data.length === 0) return [{ data: [], showSummary: true }];
  const capP1 = hasStructured ? 9 : 15;
  const capP2 = hasStructured ? 9 : 20;
  if (data.length <= capP1) return [{ data: [...data], showSummary: hasStructured }];
  const pages: PageInfo[] = [];
  let cur = 0;
  let pIdx = 0;
  while (cur < data.length) {
    const cap = pIdx === 0 ? capP1 : capP2;
    const slice = data.slice(cur, cur + cap);
    const isLast = cur + slice.length >= data.length;
    pages.push({ data: slice, showSummary: isLast && hasStructured });
    cur += slice.length;
    pIdx++;
  }
  return pages;
};

// Column widths of the five sales columns (old weight rule :579-631 over ت / المادة / الكمية / السعر المفرد / المجموع)
const COLUMN_WIDTHS = ['7.7%', '30.8%', '15.4%', '23.1%', '23.1%'];

export function SalesInvoiceA4(p: SalesInvoiceA4Props) {
  const t = useMemo(() => createT(p.locale), [p.locale]);
  const isAr = p.locale === 'ar';
  const settings = p.office;
  const hasStructured = !p.cashSale || (p.summary !== undefined && p.summary.length > 0);
  const pagesInfo = getPagesInfo(p.lines, hasStructured);
  const columns: ReadonlyArray<{ header: string; numeric: boolean }> = [
    { header: t('print.sale.col_no'), numeric: true },
    { header: t('print.sale.col_product'), numeric: false },
    { header: t('print.sale.col_qty'), numeric: true },
    { header: t('print.sale.col_price'), numeric: true },
    { header: t('print.sale.col_total'), numeric: true },
  ];
  const sideBorder: CSSProperties = { borderRight: isAr ? '1px solid #e5e7eb' : 'none', borderLeft: !isAr ? '1px solid #e5e7eb' : 'none' };

  return (
    <div id="professional-print-template" data-testid="sales-invoice-a4" className="p-0 sm:p-8 min-h-screen bg-gray-100 print:p-0" style={{ backgroundColor: '#f3f4f6' }}>
      {pagesInfo.map((pageInfo, pageIndex) => {
        const isLastPage = pageIndex === pagesInfo.length - 1;
        return (
          <div
            key={pageIndex}
            className="professional-page text-black relative mx-auto mb-8 shadow-none print:shadow-none print:mb-0 flex flex-col justify-start"
            style={{
              width: '210mm',
              height: '297mm',
              maxHeight: '297mm',
              minHeight: '297mm',
              padding: '10mm 12mm 24mm 12mm',
              backgroundColor: '#ffffff',
              color: '#000000',
              fontFamily: 'Cairo, system-ui, -apple-system, sans-serif',
              direction: isAr ? 'rtl' : 'ltr',
              pageBreakAfter: isLastPage ? 'auto' : 'always',
              pageBreakInside: 'avoid',
              breakAfter: isLastPage ? 'auto' : 'page',
              breakInside: 'avoid',
              boxSizing: 'border-box',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              position: 'relative',
              overflow: 'hidden',
              border: 'none',
              boxShadow: 'none'
            }}
          >
            {/* Header Section - Rich company & settings identity details */}
            <div className="relative z-10 mb-2 overflow-hidden flex-shrink-0">
              <div className="p-3 sm:p-3.5 rounded-2xl flex justify-between items-center bg-slate-50 border border-slate-300 shadow-xs" style={{ borderColor: '#334155', borderWidth: '1.5px' }}>
                {/* Right Side: Company details */}
                <div className="space-y-1 w-[38%] max-w-[38%]">
                  <h1 className="text-lg sm:text-xl font-black" style={{ color: '#000000', lineHeight: '1.2', letterSpacing: 'normal' }}>
                    {settings.name}
                  </h1>
                  <div className="text-[10px] font-bold text-gray-700 space-y-0.5" style={{ lineHeight: '1.3', letterSpacing: 'normal' }}>
                    {settings.address && (
                      <div className="flex items-center gap-1">
                        <span className="text-black font-black">{t('print.address_label')}</span>
                        <span>{settings.address}</span>
                      </div>
                    )}
                    {(settings.phone1 || settings.phone2) && (
                      <div className="flex items-center gap-1">
                        <span className="text-black font-black">{t('print.phone_label')}</span>
                        <span className="font-mono text-gray-800">
                          {settings.phone1} {settings.phone2 && ` - ${settings.phone2}`}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Center: System / Custom Logo - Displayed prominently on EVERY page */}
                <div className="w-[24%] flex justify-center items-center flex-shrink-0">
                  <div className="relative h-20 w-20 flex items-center justify-center">
                    {settings.logoUrl && (
                      <img
                        src={settings.logoUrl}
                        alt="Logo"
                        className="h-20 w-20 object-contain rounded-[1rem] border-2 border-black p-1 bg-white shadow-sm absolute inset-0 z-10"
                        referrerPolicy="no-referrer"
                        crossOrigin="anonymous"
                      />
                    )}
                    <div className="h-20 w-20 rounded-[1rem] border-2 border-black flex flex-col items-center justify-center bg-black text-white font-black shadow-sm p-1 text-center select-none z-0">
                      <span className="text-[12px] leading-tight font-extrabold tracking-tighter">
                        {settings.name ? (settings.name.length > 10 ? settings.name.substring(0, 8) + '..' : settings.name) : t('print.logo_fallback_name')}
                      </span>
                      <span className="text-[8px] opacity-80 mt-0.5 font-bold uppercase tracking-widest">
                        {t('print.logo_fallback_sub')}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Left Side: Document Metadata & Issue details */}
                <div className="text-right space-y-1 w-[38%] max-w-[38%]">
                  <div className="bg-black text-white px-3 py-1 font-black text-xs inline-block shadow-sm rounded-lg" style={{ letterSpacing: 'normal' }}>
                    {isAr ? (p.buyerName || p.title) : (p.buyerName || p.title).toUpperCase()}
                  </div>
                  <div className="text-[9px] font-bold text-gray-700 space-y-0.5" style={{ lineHeight: '1.3', letterSpacing: 'normal' }}>
                    <div>
                      <span className="text-black font-black">{t('print.issue_date')}</span>
                      <span className="font-mono ml-1 mr-1">{p.dateLabel}</span>
                    </div>
                    <div>
                      <span className="text-black font-black">{t('print.doc_no')}</span>
                      <span className="font-mono ml-1 mr-1">#{p.docNumber}</span>
                    </div>
                    {p.buyerPhone && (
                      <div>
                        <span className="text-black font-black">{t('print.sale.buyer_phone')}</span>
                        <span className="font-mono ml-1 mr-1" dir="ltr">{p.buyerPhone}</span>
                      </div>
                    )}
                    {p.buyerAddress && (
                      <div>
                        <span className="text-black font-black">{t('print.sale.address')}</span>
                        <span className="ml-1 mr-1">{p.buyerAddress}</span>
                      </div>
                    )}
                    {settings.managerName && (
                      <div>
                        <span className="text-black font-black">{t('print.manager')}</span>
                        <span>{settings.managerName}</span>
                      </div>
                    )}
                    {settings.accountantName && (
                      <div>
                        <span className="text-black font-black">{t('print.accountant')}</span>
                        <span>{settings.accountantName}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Top Details & Summary Section (sales invoice, first page only) */}
            {pageIndex === 0 && (
              <div className="mb-2 relative z-10 flex-shrink-0">
                <div className="border border-slate-700 rounded-xl overflow-hidden bg-white shadow-xs">
                  {/* Banner Header */}
                  <div className="bg-[#111827] text-white text-center py-1 text-[11px] font-black uppercase tracking-wide">
                    {t('print.sale.box_title')}
                  </div>

                  {/* Main 4-Box Summary Grid */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-y sm:divide-y-0 divide-gray-200 text-[10px]" style={{ direction: isAr ? 'rtl' : 'ltr' }}>
                    {/* 1. Buyer / Customer */}
                    <div className="p-2.5 bg-gray-50/80 flex flex-col justify-center min-h-[50px] overflow-visible">
                      <span className="text-gray-500 font-bold text-[9.5px] leading-tight mb-1">{t('print.sale.buyer')}</span>
                      <span className="font-black text-gray-900 text-xs leading-relaxed break-words" style={{ lineHeight: '1.4' }}>{p.buyerName}</span>
                    </div>

                    {/* 2. Doc / Invoice Number */}
                    <div className="p-2.5 bg-white flex flex-col justify-center min-h-[50px] overflow-visible" style={sideBorder}>
                      <span className="text-gray-500 font-bold text-[9.5px] leading-tight mb-1">{t('print.sale.doc')}</span>
                      <span className="font-black text-gray-900 font-mono text-xs leading-relaxed" dir="ltr" style={{ lineHeight: '1.4' }}>#{p.docNumber}</span>
                    </div>

                    {/* 3. Payment Method */}
                    <div className="p-2.5 bg-gray-50/80 flex flex-col justify-center min-h-[50px] overflow-visible" style={sideBorder}>
                      <span className="text-gray-500 font-bold text-[9.5px] leading-tight mb-1">{t('print.sale.payment')}</span>
                      <span className={`font-black text-[11px] leading-relaxed ${p.cashSale ? 'text-emerald-700' : 'text-amber-800'}`} style={{ lineHeight: '1.4' }}>
                        {p.paymentMethodLabel}
                      </span>
                    </div>

                    {/* 4. Grand Total */}
                    <div className="p-2.5 bg-slate-100 flex flex-col justify-center items-center text-center min-h-[50px] overflow-visible" style={sideBorder}>
                      <span className="text-gray-600 font-bold text-[9.5px] leading-tight mb-1">{t('print.sale.grand_total')}</span>
                      <span className="font-black font-mono text-xs text-gray-900 leading-relaxed" dir="ltr" style={{ lineHeight: '1.4' }} data-testid="sales-invoice-total">{p.grandTotalLabel}</span>
                    </div>
                  </div>

                  {/* Dynamic Extra Line (Phone, Address, Cash Received, Remaining) */}
                  {(p.buyerPhone || p.buyerAddress || p.receivedLabel || p.remainingLabel) && (
                    <div className="border-t border-gray-200 grid grid-cols-2 sm:grid-cols-4 divide-x divide-gray-200 text-[9.5px] bg-gray-50/40" style={{ direction: isAr ? 'rtl' : 'ltr' }}>
                      {p.buyerPhone && (
                        <div className="p-2 px-2.5 flex items-center justify-between gap-1">
                          <span className="text-gray-500 font-bold">{t('print.sale.phone')}</span>
                          <span className="font-mono font-bold text-gray-800" dir="ltr">{p.buyerPhone}</span>
                        </div>
                      )}
                      {p.buyerAddress && (
                        <div className="p-2 px-2.5 flex items-center justify-between gap-1" style={sideBorder}>
                          <span className="text-gray-500 font-bold">{t('print.sale.address')}</span>
                          <span className="font-bold text-gray-800 break-words leading-relaxed">{p.buyerAddress}</span>
                        </div>
                      )}
                      {p.receivedLabel && (
                        <div className="p-2 px-2.5 flex items-center justify-between gap-1" style={sideBorder}>
                          <span className="text-emerald-700 font-bold">{t('print.sale.received')}</span>
                          <span className="font-mono font-bold text-emerald-800" dir="ltr">{p.receivedLabel}</span>
                        </div>
                      )}
                      {p.remainingLabel && (
                        <div className="p-2 px-2.5 flex items-center justify-between gap-1" style={sideBorder}>
                          <span className="text-rose-700 font-bold">{t('print.sale.remaining')}</span>
                          <span className="font-mono font-bold text-rose-800" dir="ltr">{p.remainingLabel}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* [C35] Tafqeet in Words (:1082-1087) */}
                  {p.wordsLabel && (
                    <div className="border-t border-gray-200 bg-white py-1 px-3 text-center text-[10px] font-bold text-gray-800" data-testid="sales-invoice-words">
                      <span className="text-gray-500 ml-1 mr-1">{t('print.sale.words')}</span>
                      <span className="text-gray-900 font-black">{p.wordsLabel}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Content Table Container */}
            <div className="mb-2 relative z-10 w-full flex-shrink-0">
              <table className="w-full border-collapse text-[10.5px] h-auto" style={{ border: '1.5px solid #000000', tableLayout: 'fixed' }}>
                <thead>
                  <tr style={{ backgroundColor: '#111827', color: '#ffffff' }}>
                    {columns.map((col, i) => (
                      <th
                        key={i}
                        className="py-1.5 px-1 text-center font-black border border-black align-middle text-[10.5px]"
                        style={{
                          border: '1.5px solid #000000',
                          width: COLUMN_WIDTHS[i],
                          letterSpacing: '0px',
                          whiteSpace: 'nowrap',
                          fontFamily: 'Cairo, system-ui, sans-serif',
                          textRendering: 'optimizeLegibility',
                          fontFeatureSettings: '"liga" 1, "calt" 1'
                        }}
                      >
                        <div className="w-full h-full flex items-center justify-center text-center">{col.header}</div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pageInfo.data.length === 0 ? (
                    <tr>
                      <td colSpan={columns.length} className="p-3 text-center text-gray-500 font-bold border border-black align-middle" style={{ border: '1px solid #000000', letterSpacing: '0px' }}>
                        {t('print.no_movements')}
                      </td>
                    </tr>
                  ) : (
                    pageInfo.data.map((line, i) => {
                      const rowNo = pagesInfo.slice(0, pageIndex).reduce((n, pg) => n + pg.data.length, 0) + i + 1;
                      const cells = [String(rowNo), line.name, line.quantity, line.priceLabel, line.totalLabel];
                      return (
                        <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-100'} style={{ pageBreakInside: 'avoid', breakInside: 'avoid' }}>
                          {cells.map((cellValue, j) => {
                            const isNum = columns[j]!.numeric;
                            const cellStyle: CSSProperties = {
                              border: '1px solid #000000',
                              width: COLUMN_WIDTHS[j],
                              letterSpacing: '0px',
                              wordBreak: 'normal',
                              overflowWrap: 'break-word',
                              whiteSpace: 'pre-line',
                              direction: isNum ? 'ltr' : (isAr ? 'rtl' : 'ltr'),
                              unicodeBidi: isNum ? 'normal' : 'plaintext',
                              lineHeight: '1.3',
                              fontFeatureSettings: '"liga" 1, "calt" 1',
                              textRendering: 'optimizeLegibility',
                              verticalAlign: 'middle',
                              textAlign: 'center'
                            };
                            return (
                              <td key={j} className="py-1.5 px-1 text-center font-medium border border-black align-middle text-[10.5px]" style={cellStyle}>
                                <div className="w-full h-full flex items-center justify-center text-center">
                                  <span
                                    dir={isNum ? 'ltr' : 'auto'}
                                    className="inline-block max-w-full select-text text-center"
                                    style={{ direction: isNum ? 'ltr' : (isAr ? 'rtl' : 'ltr'), unicodeBidi: 'plaintext', letterSpacing: '0px', fontFeatureSettings: '"liga" 1, "calt" 1', textAlign: 'center' }}
                                  >
                                    {cellValue || '-'}
                                  </span>
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Bottom Section (summary box) - plain label/value grid (:1546-1561) */}
            {pageInfo.showSummary && p.summary && p.summary.length > 0 && (
              <div className="mt-auto relative z-10 pt-1 pb-0 mb-3 flex-shrink-0" style={{ pageBreakInside: 'avoid', breakInside: 'avoid', marginBottom: '4mm' }}>
                <div className="border-2 rounded-xl overflow-hidden shadow-sm bg-white" style={{ borderColor: '#000000' }}>
                  <div className="bg-black text-white text-center py-1 text-[11px] font-black uppercase tracking-wide">
                    {t('print.summary_title')}
                  </div>
                  <div className="grid grid-cols-2 divide-x divide-black border-t border-black bg-gray-50/50" style={{ direction: isAr ? 'rtl' : 'ltr' }}>
                    {p.summary.map((item, idx) => (
                      <div key={idx} className="p-2 flex justify-between items-center bg-white" style={{ borderRight: isAr && idx % 2 === 1 ? '1.5px solid #000000' : 'none', borderLeft: !isAr && idx % 2 === 1 ? '1.5px solid #000000' : 'none' }}>
                        <span className="font-black text-gray-800 text-[10px]">{item.label}:</span>
                        <span className="font-mono font-black text-xs text-black" dir="ltr">{String(item.value)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Bottom Signatures for Sales Invoices */}
            {isLastPage && (
              <div className="mt-auto relative z-10 pt-4 pb-2 mb-3 flex justify-between items-center text-[11px] font-bold text-gray-900 border-t border-gray-300 flex-shrink-0 w-full" style={{ letterSpacing: 'normal' }}>
                <div className="flex flex-col items-center px-4">
                  <span className="text-gray-900 font-bold mb-7">{t('print.sale.sign_buyer')}</span>
                  <span className="text-gray-400 font-mono tracking-widest text-[11px] select-none">........................................</span>
                </div>
                <div className="flex flex-col items-center px-4">
                  <span className="text-gray-900 font-bold mb-7">{t('print.sale.sign_cashier')}</span>
                  <span className="text-gray-400 font-mono tracking-widest text-[11px] select-none">........................................</span>
                </div>
              </div>
            )}

            {/* Footer at the absolute A4 limit */}
            <div className="absolute bottom-3 left-12 right-12 border-t-2 pt-1.5 flex justify-between items-center text-[9.5px] font-bold" style={{ borderTopColor: '#000000', color: '#111827', fontFamily: 'Cairo, sans-serif', letterSpacing: 'normal' }}>
              <p className="text-black">{settings.name} - {t('print.footer_system')}</p>
              <div className="flex items-center gap-1 text-black font-semibold">
                <span>{t('print.page')}</span>
                <span className="font-mono font-bold">{pageIndex + 1}</span>
                <span>{t('print.page_of')}</span>
                <span className="font-mono font-bold">{pagesInfo.length}</span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
